import {ChangeDetectionStrategy, Component, computed, inject} from '@angular/core';
import {RouterLink} from '@angular/router';

import {UiButtonComponent} from '../../shared/ui';
import {ConsumptionCheckStore} from '../../services/consumption/consumption-check-store.service';
import {QuickCheckRow, daysInStock} from '../../models/consumption-check';

/** How many checks the teaser lists before it just shows the count. */
const TEASER_LIMIT = 3;

/**
 * Home widget for open consumption reminders. Renders nothing at all when the
 * round is empty — an empty quick-check list is the feature working, and it
 * should not take up space saying so.
 *
 * Reads the shared store rather than fetching: the store already loads per
 * household and follows the live stream, so the teaser, the nav badge and the
 * page never disagree.
 */
@Component({
  selector: 'app-quick-check-teaser',
  standalone: true,
  imports: [RouterLink, UiButtonComponent],
  templateUrl: './quick-check-teaser.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuickCheckTeaserComponent {
  private readonly store = inject(ConsumptionCheckStore);

  readonly openCount = this.store.openCount;

  readonly rows = computed<QuickCheckRow[]>(() =>
    this.store.rows().filter(r => r.status !== 'settled').slice(0, TEASER_LIMIT),
  );

  protected ageLabel(row: QuickCheckRow): string {
    const days = daysInStock(row.check.stock);
    const where = row.check.stock?.storeName;
    if (days == null) return where ?? '';
    const age = days === 1 ? '1 day' : `${days} days`;
    return where ? `${age} in ${where}` : `${age} in stock`;
  }

  protected placeholderImage(row: QuickCheckRow): string {
    const name = encodeURIComponent(row.check.stock?.productName ?? '?');
    return `https://placehold.co/96x96/EEE/31343C?font=noto-sans&text=${name}`;
  }

  protected trackRow(_index: number, row: QuickCheckRow): number {
    return row.check.id;
  }
}
